import { useEffect, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Input } from "../components/ui/input"
import { cn } from "../lib/utils"
import type { CogoElement } from "../core/types"
import { readResolvedArray, recordValue, shorthandId } from "./property-value-utils"
import { liftV4Props } from "./element-renderer"

export function ComboboxRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  const elementRecord = element as unknown as Record<string, unknown>
  const authoredOpenRaw = elementRecord.open ?? elementRecord.defaultOpen
  // Only elements that explicitly author `open`/`defaultOpen` are snapshot-driven
  // (each incoming snapshot reasserts their open state). Elements with no authored
  // open intent keep `open` as purely-local UI state that survives re-renders.
  const hasAuthoredOpen = elementRecord.open !== undefined
    || elementRecord.defaultOpen !== undefined
  const authoredOpen = authoredOpenRaw === true
    || authoredOpenRaw === "true"
    || (typeof authoredOpenRaw === "string"
      && resolveSymbol(authoredOpenRaw) === true)
  const authoredSearchRaw = elementRecord.searchQuery
  const authoredSearch = authoredSearchRaw == null
    ? ""
    : String(
        typeof authoredSearchRaw === "string"
          ? (resolveSymbol(authoredSearchRaw) ?? authoredSearchRaw)
          : authoredSearchRaw,
      )
  const [open, setOpen] = useState(authoredOpen)
  const [search, setSearch] = useState(authoredSearch)

  // Reassert the popover open state from each incoming snapshot ONLY when the
  // element explicitly authors `open`/`defaultOpen`. Keying an unconditional
  // resync off `element` (a fresh object on every parent re-render) force-closed
  // dropdowns the user had just opened when no open intent was authored — that
  // regressed the booking reopen flow (#1798). Elements without authored open
  // keep `open` as purely-local UI state that survives re-renders.
  useEffect(() => {
    if (hasAuthoredOpen) setOpen(authoredOpen)
  }, [element, authoredOpen, hasAuthoredOpen])

  useEffect(() => {
    setSearch(authoredSearch)
  }, [authoredSearch])

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Search..."

  const [selectedValue, setSelectedValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  const explicitOptions = element.children ?? []
  // JITRULE Task 2: authored children skip the recordValue() shorthand path
  // below, so lift them directly — same rule, single interpreter.
  const options = explicitOptions.length > 0
    ? explicitOptions.map(liftV4Props)
    : readResolvedArray(element, "options", resolveSymbol).map((raw, index) => {
        const option = recordValue(raw)
        return {
          ...option,
          id: String(option.id ?? shorthandId(element.id, "options", index)),
          type: "option",
          text: option.label ?? option.text ?? option.value,
          variant: String(option.value ?? option.id ?? index),
        } as unknown as CogoElement
      })
  const filtered = options.filter((opt) => {
    const label = (opt.text ?? "").toLowerCase()
    return !search || label.includes(search.toLowerCase())
  })

  // Find display label for selected value
  const optionValue = (option: CogoElement) => String(
    option.value ?? option.variant ?? option.text ?? option.id,
  )
  const selectedLabel = options.find(
    (option) => optionValue(option) === selectedValue,
  )?.text ?? ""

  const handleSelect = (opt: typeof options[number]) => {
    const value = optionValue(opt)
    setSelectedValue(value)
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
    setOpen(false)
    setSearch("")
  }

  return (
    <div
      className={cn("cn-combobox relative", element.className)}
      data-testid={`combobox-${element.id}`}
      data-value={selectedValue}
      data-open={open ? "true" : "false"}
      data-search-query={search}
      data-options={JSON.stringify(options.map((option) => ({
        value: optionValue(option),
        label: String(option.text ?? option.label ?? optionValue(option)),
      })))}
    >
      <button
        type="button"
        data-slot="combobox-trigger"
        className="cn-combobox-trigger cn-input flex h-10 w-full items-center justify-between"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={element.ariaLabel ?? placeholder}
      >
        <span className={cn("cn-combobox-value", selectedValue ? "text-foreground" : "text-muted-foreground")}>
          {selectedValue ? selectedLabel || selectedValue : placeholder}
        </span>
        <svg
          className={cn("ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform", open && "rotate-180")}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="cn-popover absolute z-50 mt-1 w-full">
          <div className="p-1">
            <Input
              type="text"
              value={search}
              onChange={(e) => {
                const query = e.target.value
                setSearch(query)
                dispatchEvent(element.id, "searchChange", {
                  ...(itemContext ?? {}),
                  query,
                  searchQuery: query,
                })
              }}
              placeholder={placeholder}
              className="h-8"
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                No results found.
              </div>
            ) : (
              filtered.map((opt) => {
                const label = opt.text ? String(resolveSymbol(opt.text) ?? "") : opt.id
                const optValue = optionValue(opt)
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-slot="combobox-item"
                    className={cn(
                      "cn-select-item relative flex w-full cursor-default select-none items-center",
                      selectedValue === optValue && "bg-accent",
                    )}
                    onClick={() => handleSelect(opt)}
                  >
                    {label}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
